import React from "react";
import toast, { type ToastPosition } from "react-hot-toast";

/**
 * 「○○まであと N 日」のような **時限的なマイルストーン通知**。
 *
 * 旧 `Home.tsx` の `toast.custom()` 実装をグローバル化したもの。
 *  - 白背景の SOREMO 標準スタイル
 *  - `text-focus-in` で blur フェードイン、duration 直前に `text-blur-out`
 *    で blur フェードアウト
 *  - 既定で `top-right` に表示（`SoremoToaster` と同位置）
 *  - `react-hot-toast` の `<Toaster />` が `position: fixed` で配置するため、
 *    スクロールしても画面右上に追従する
 *
 * 旧実装は `document.querySelector('.text-focus-in')` で DOM を直接探して
 * 退場アニメ用クラスを後付けしていた。これは複数同時表示で誤動作するため、
 * `useState` + `data-milestone-id` ベースに置き換えている。
 */
export interface MilestoneToastOptions {
  /** 上段の小ラベル（例: "進捗マイルストーンまであと"）。 */
  label: string;
  /** 大きく表示する日数。 */
  days: number;
  /** 単位ラベル（例: "日"）。既定 "日"。 */
  unit?: string;
  /** 表示時間 (ms)。既定 6200（5000ms 表示 + 1200ms 退場アニメ）。 */
  duration?: number;
  /** 退場アニメを開始するまでの時間 (ms)。既定 5000。 */
  exitAfter?: number;
  /** 表示位置。既定 "bottom-left"。 */
  position?: ToastPosition;
}

const MilestoneToastBody: React.FC<{
  label: string;
  days: number;
  unit: string;
  exiting: boolean;
}> = ({ label, days, unit, exiting }) => (
  <div
    className={exiting ? "text-blur-out" : "text-focus-in"}
    style={{
      background: "#fff",
      padding: "16px 20px",
      borderRadius: "6px",
      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
      minWidth: 200,
    }}
  >
    <ul className="u-col" style={{ listStyle: "none", margin: 0, padding: 0 }}>
      <li className="bodytext-13 u-line-height-200">{label}</li>
      <li
        className="heading-spacing"
        style={{ fontSize: "48px", lineHeight: 1.1 }}
      >
        {days}
        <span className="bodytext-11 u-text-light-gray">{unit}</span>
      </li>
    </ul>
  </div>
);

/**
 * マイルストーントーストを表示する。
 *
 * 内部で `toast.custom()` を呼び出し、退場アニメは `data-milestone-id` を付け
 * 単一トースト単位で正確に解決する。退場アニメ完了後に自動で `toast.dismiss` する。
 */
export const showMilestoneToast = ({
  label,
  days,
  unit = "日",
  duration = 6200,
  exitAfter = 5000,
  position = "bottom-left",
}: MilestoneToastOptions): string => {
  const toastId = `milestone-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

  toast.custom(
    () => <MilestoneToastBody label={label} days={days} unit={unit} exiting={false} />,
    {
      id: toastId,
      duration,
      position,
    },
  );

  // 退場アニメは「同じ id で `exiting: true` の本体に差し替え」で実現する。
  // DOM querySelector に頼らないので複数トーストが並んでも誤動作しない。
  window.setTimeout(() => {
    toast.custom(
      () => <MilestoneToastBody label={label} days={days} unit={unit} exiting />,
      {
        id: toastId,
        duration: duration - exitAfter,
        position,
      },
    );
  }, exitAfter);

  return toastId;
};

export default showMilestoneToast;
